/**
 * Bot AI: a player that issues Commands through the same executeCommand path a
 * human uses. This proves the command-pattern design — the bot is literally
 * "another player," and future networked humans are the same thing over a wire.
 *
 * Strategy is intentionally simple but complete: grow economy, build houses +
 * barracks, train an army, then attack. Good enough to make the MVP playable.
 */
import type { World } from "../sim/world";
import type { Registry } from "../data/registry";
import type { BuildingDef } from "../data/schema";
import type { Command, Eid } from "../sim/types";
import { canGatherNode } from "../sim/naval";
import { planArmy, chooseUnit } from "./tactics";

interface BotMemory {
  nextThink: number;
  nextExpand: number;
  workerTarget: number;
  armyTarget: number;
  attackWaveSent: boolean;
  assignedWorkers: Set<Eid>;
  /** Army units that have been COMMITTED to the offensive. Fresh units muster at
   *  home until a full batch is ready, then join — so reinforcements arrive as a
   *  group instead of trickling in one-by-one to be picked off. */
  committed: Set<Eid>;
}

/** Serializable decision state required for a saved bot match to continue with
 * the same cadence, worker assignments, and attack-wave commitments. */
export interface BotState {
  nextThink: number;
  nextExpand: number;
  workerTarget: number;
  armyTarget: number;
  attackWaveSent: boolean;
  assignedWorkers: Eid[];
  committed: Eid[];
}

/** The bot's building roles are inferred from resolved data, rather than names.
 * A mod can replace any of these with a new id as long as it keeps the gameplay
 * property that makes the role useful. */
interface BuildingRoles {
  townCenter: string[];
  housing: string[];
  barracks: string[];
  stables: string[];
  temple: string[];
  tower: string[];
  field: string[];
  storage: string[];
}

function memoryFrom(state?: BotState): BotMemory {
  if (!state) {
    return {
      nextThink: 0,
      nextExpand: 0,
      workerTarget: 8,
      armyTarget: 7,
      attackWaveSent: false,
      assignedWorkers: new Set(),
      committed: new Set(),
    };
  }
  return {
    nextThink: state.nextThink,
    nextExpand: state.nextExpand,
    workerTarget: state.workerTarget,
    armyTarget: state.armyTarget,
    attackWaveSent: state.attackWaveSent,
    assignedWorkers: new Set(state.assignedWorkers),
    committed: new Set(state.committed),
  };
}

export class Bot {
  readonly player: number;
  private mem: BotMemory;
  /** interval (sim seconds) between decisions */
  private interval = 0.5;

  constructor(player: number, state?: BotState) {
    this.player = player;
    this.mem = memoryFrom(state);
  }

  snapshotState(): BotState {
    return {
      nextThink: this.mem.nextThink,
      nextExpand: this.mem.nextExpand,
      workerTarget: this.mem.workerTarget,
      armyTarget: this.mem.armyTarget,
      attackWaveSent: this.mem.attackWaveSent,
      assignedWorkers: [...this.mem.assignedWorkers].sort((a, b) => a - b),
      committed: [...this.mem.committed].sort((a, b) => a - b),
    };
  }

  think(world: World, reg: Registry, t: number, dispatch: (cmd: Command) => void) {
    if (t < this.mem.nextThink) return;
    this.mem.nextThink = t + this.interval;
    const ps = world.players.get(this.player);
    if (!ps || ps.defeated) return;

    const roles = this.buildingRoles(reg, ps.civ);
    const my = this.myEntities(world, roles);
    const enemyTc = this.enemyTarget(world);
    const housing = this.nextBuilding(world, roles.housing);
    const barracks = this.nextBuilding(world, roles.barracks);
    const stables = this.nextBuilding(world, roles.stables);
    const temple = this.nextBuilding(world, roles.temple);
    const tower = this.nextBuilding(world, roles.tower);
    const field = this.nextBuilding(world, roles.field);
    const storage = this.nextBuilding(world, roles.storage);

    // 1) assign idle workers to gather, balancing across resource types so the
    //    bot has wood for buildings + gold for elite units, not just food.
    let idx = 0;
    for (const w of my.workers) {
      const g = world.gatherer.get(w);
      const mv = world.move.get(w);
      const b = world.builder.get(w);
      if (!g || !mv) { idx++; continue; }
      if (b?.foundation) { idx++; continue; } // busy building
      if (g.node && world.resource.has(g.node)) { idx++; continue; } // already working
      // Always keep ~half the workers on FOOD (needed to train workers + army);
      // skew the other half to wood (more so when wood-starved) and a little gold
      // once the economy is sizable. Flooding everything to wood starved food and
      // froze worker production, so the split is kept balanced.
      const woodLow = (ps.resources["wood"] ?? 0) < 60;
      const stoneLow = (ps.resources["stone"] ?? 0) < 100; // for towers/walls
      let prefer: string;
      if (idx % 2 === 0) prefer = "food";
      else if (woodLow) prefer = "wood";
      else if (stoneLow && my.workers.length >= 8 && idx % 6 === 1) prefer = "stone"; // a little stone for defences
      else prefer = my.workers.length >= 8 && idx % 4 === 3 ? "gold" : "wood";
      const node = this.bestNode(world, w, prefer);
      if (node !== null) {
        dispatch({ type: "gather", player: this.player, entities: [w], target: node });
      }
      idx++;
    }

    // 1.5) keep every foundation staffed — the bot used to place foundations and
    //       then let the builder wander off, leaving sites half-built forever.
    for (const f of my.foundations) {
      let staffed = false;
      for (const wId of my.workers) {
        if (world.builder.get(wId)?.foundation === f) { staffed = true; break; }
      }
      if (staffed) continue;
      const ft = world.transform.get(f);
      if (!ft) continue;
      let pick: Eid | null = null, pd = Infinity;
      for (const wId of my.workers) {
        if (world.builder.get(wId)?.foundation != null) continue; // already building elsewhere
        const wt = world.transform.get(wId);
        if (!wt) continue;
        const d = (wt.pos.x - ft.pos.x) ** 2 + (wt.pos.y - ft.pos.y) ** 2;
        if (d < pd) { pd = d; pick = wId; }
      }
      if (pick !== null) dispatch({ type: "construct", player: this.player, workers: [pick], target: f });
    }

    // 2) train workers — scale the target with population cap (~60% of cap, hard
    //    max 40) so the bot keeps growing its economy as houses raise the cap,
    //    instead of plateauing at a fixed 8 villagers. Leaves headroom for army.
    const workerTarget = Math.min(40, Math.max(this.mem.workerTarget, Math.floor(ps.populationCap * 0.6)));
    const tcQueue = my.townCenters[0] ? world.producer.get(my.townCenters[0])?.queue.length ?? 0 : 0;
    if (my.workers.length < workerTarget && my.townCenters.length > 0 && tcQueue < 2 && ps.population < ps.populationCap) {
      const role = reg.roleUnit(ps.civ, "worker");
      if (role) {
        dispatch({ type: "train", player: this.player, building: my.townCenters[0], unitId: role.id });
      }
    }

    // ONE foundation of a given type may be in flight at a time — the bot used to
    // re-issue a house every think tick while the last was still building, which
    // is why bases ended up wall-to-wall houses and nothing else.
    const pending = (id: string | null) => !!id && my.foundations.some((f) => world.ident.get(f)?.defId === id);

    // 3) HOUSES — stay ahead of the pop cap, but only one at a time so the bot
    //    doesn't spam them. Loose headroom target scales a touch with cap.
    const headroom = ps.populationCap - ps.population;
    if (housing && headroom <= 4 && !pending(housing) && ps.populationCap < 90 && my.townCenters.length > 0) {
      this.buildNear(world, reg, my, housing, dispatch);
    }

    // 4) BARRACKS — scale with the economy (~one per 45 pop cap, min 1 once the
    //    base can sustain it), so a bigger base fields a bigger army instead of
    //    bottlenecking on a single barracks.
    const barracksTarget = ps.populationCap >= 15 ? 1 + Math.floor(ps.populationCap / 45) : 0;
    if (barracks && my.barracks.length < barracksTarget && !pending(barracks) && (ps.resources["wood"] ?? 0) >= 150 && my.townCenters.length > 0) {
      this.buildNear(world, reg, my, barracks, dispatch);
    }

    // 4.1) STABLES — diversify into CAVALRY early once there's a barracks, so the
    //      army isn't all infantry (a 5-year-old fields horses; so does the bot).
    if (stables && my.stables.length < 1 && !pending(stables) && my.barracks.length > 0 &&
        ps.populationCap >= 18 && (ps.resources["wood"] ?? 0) >= 150 && my.townCenters.length > 0) {
      this.buildNear(world, reg, my, stables, dispatch);
    }

    // 4.2) TEMPLE — a healer building for army SUSTAIN, soon after the barracks
    //      (priests trail the attack and keep it alive — see the attack step).
    if (temple && my.temples.length < 1 && !pending(temple) && my.barracks.length > 0 &&
        ps.populationCap >= 24 && (ps.resources["wood"] ?? 0) >= 120 && (ps.resources["gold"] ?? 0) >= 40 && my.townCenters.length > 0) {
      this.buildNear(world, reg, my, temple, dispatch);
    }

    // 4.3) TOWERS — point defense around the base, so raids on the economy get
    //      punished. Garrisoned below so they actually fire. Needs stone (gathered
    //      in step 1 once the base is sizable).
    const towerTarget = ps.populationCap >= 20 ? Math.min(3, 1 + Math.floor(ps.populationCap / 35)) : 0;
    if (tower && my.towers.length < towerTarget && !pending(tower) && my.builders.length > 0 &&
        (ps.resources["stone"] ?? 0) >= 80 && (ps.resources["wood"] ?? 0) >= 40 && my.townCenters.length > 0) {
      this.buildNear(world, reg, my, tower, dispatch);
    }

    // 4.35) GARRISON towers — an empty tower holds its fire, so put a worker in each
    //       finished one (1 arrow of defence for a tiny economy hit).
    for (const tw of my.towers) {
      const gar = world.garrison.get(tw);
      if (!gar || gar.occupants.length > 0) continue;
      const tt = world.transform.get(tw);
      let pick: Eid | null = null, pd = Infinity;
      for (const wId of my.workers) {
        if (world.builder.get(wId)?.foundation != null) continue; // not a builder mid-job
        const g = world.gatherer.get(wId);
        if (g?.state === "gathering") continue; // leave active gatherers be
        const wt = world.transform.get(wId);
        if (!wt || !tt) continue;
        const d = (wt.pos.x - tt.pos.x) ** 2 + (wt.pos.y - tt.pos.y) ** 2;
        if (d < pd) { pd = d; pick = wId; }
      }
      if (pick !== null) dispatch({ type: "embark", player: this.player, entities: [pick], transport: tw });
    }

    // 4.7) FIELDS: a couple of farms near the base give steady close-to-home food
    //      (each staffs up to 2 farmers via the field's gather cap).
    const pendingField = pending(field);
    if (
      field && my.workers.length >= 6 && my.fields.length < 2 && !pendingField &&
      my.builders.length > 0 && my.townCenters.length > 0 && (ps.resources["wood"] ?? 0) >= 60
    ) {
      this.buildNear(world, reg, my, field, dispatch);
    }

    // 4.5) rally the TC onto a resource so freshly trained workers auto-gather
    //      instead of milling at home.
    if (my.townCenters.length > 0 && !world.rally.has(my.townCenters[0])) {
      const node = this.bestNode(world, my.townCenters[0], "food");
      const nt = node !== null ? world.transform.get(node) : null;
      if (node !== null && nt) {
        dispatch({ type: "setRally", player: this.player, building: my.townCenters[0], x: nt.pos.x, y: nt.pos.y, resource: node });
      }
    }

    // 4.6) EXPAND: if the bot's workers are hauling resources a long way back to
    //      a drop-off, drop a storage pit right by the cluster they're actually
    //      working. Only blocked by a pending STORAGE (not houses), or it would
    //      never fire — there's almost always a house under construction.
    const pendingStorage = pending(storage);
    const tile = Math.sqrt(3) * world.size;
    if (
      storage && t >= this.mem.nextExpand && my.workers.length >= 4 && !pendingStorage &&
      my.builders.length > 0 && my.storagePits.length < 4 && (ps.resources["wood"] ?? 0) >= 75
    ) {
      // farthest-from-drop-off node that a worker is currently gathering
      let far: { x: number; y: number } | null = null;
      let farD = (tile * 5) ** 2; // worth a forward base past ~5 tiles of hauling
      for (const wId of my.workers) {
        const g = world.gatherer.get(wId);
        if (!g || g.node === null) continue;
        const nt = world.transform.get(g.node);
        if (!nt) continue;
        let nearest = Infinity;
        for (const ds of my.dropSites) {
          const d = (ds.x - nt.pos.x) ** 2 + (ds.y - nt.pos.y) ** 2;
          if (d < nearest) nearest = d;
        }
        if (nearest > farD) { farD = nearest; far = { x: nt.pos.x, y: nt.pos.y }; }
      }
      if (far) { this.mem.nextExpand = t + 15; this.buildNear(world, reg, my, storage, dispatch, far); }
    }

    // 5) train army from EVERY barracks and stables with a short queue, so all
    //    production buildings actually produce (not just the first). The train
    //    command itself re-checks cost + pop cap, so over-dispatching is safe.
    if (ps.population < ps.populationCap) {
      // train a BALANCED, COUNTER-AWARE mix from each idle production building:
      // chooseUnit targets class ratios and shifts them against the player's army
      // (e.g. lots of archers → it skews to cavalry), and fields elites when affordable.
      for (const b of [...my.barracks, ...my.stables]) {
        if ((world.producer.get(b)?.queue.length ?? 0) >= 2) continue;
        const defId = world.ident.get(b)?.defId;
        if (!defId) continue;
        const unitId = chooseUnit(world, reg, this.player, defId);
        if (unitId) dispatch({ type: "train", player: this.player, building: b, unitId });
      }
      // priests scale with army size (~1 per 8 fighters, capped) for healing sustain.
      const priestTarget = Math.min(4, Math.max(1, Math.floor(my.army.length / 8)));
      if (my.priests.length < priestTarget) {
        for (const tmp of my.temples) {
          if ((world.producer.get(tmp)?.queue.length ?? 0) > 0) continue;
          const pr = reg.unitsOf(ps.civ).find((u) => u.class === "priest");
          if (pr) dispatch({ type: "train", player: this.player, building: tmp, unitId: pr.id });
        }
      }
    }

    // 6) ATTACK in WAVES — MUSTER then commit in BATCHES, so reinforcements arrive
    //    as a group instead of trickling in one-by-one to be shot down at the wall.
    //    Fresh units rally at home; once a full wave (or a half-wave reinforcement
    //    for a live assault) has gathered they join the COMMITTED force, which fights
    //    as squads (planArmy: proportional counter-detachments for threats, the rest
    //    on the best objective). Priests trail the committed force and heal it.
    const army = my.army;
    const live = new Set(army);
    for (const u of [...this.mem.committed]) if (!live.has(u)) this.mem.committed.delete(u); // prune the fallen
    const committedNow = army.filter((u) => this.mem.committed.has(u));
    const fresh = army.filter((u) => !this.mem.committed.has(u));
    const batch = Math.max(2, Math.ceil(this.mem.armyTarget / 2));
    if (fresh.length >= this.mem.armyTarget || (committedNow.length > 0 && fresh.length >= batch)) {
      for (const u of fresh) this.mem.committed.add(u); // launch / reinforce
    }
    const force = army.filter((u) => this.mem.committed.has(u));
    if (force.length > 0) {
      const plans = planArmy(world, this.player, force);
      if (plans.length > 0) {
        for (const p of plans) dispatch({ type: "attack", player: this.player, entities: p.units, target: p.target });
      } else if (enemyTc) {
        dispatch({ type: "attackMove", player: this.player, entities: force, x: enemyTc.x, y: enemyTc.y });
      }
      if (my.priests.length > 0) {
        let cx = 0, cy = 0;
        for (const u of force) { const t = world.transform.get(u); if (t) { cx += t.pos.x; cy += t.pos.y; } }
        cx /= force.length; cy /= force.length;
        dispatch({ type: "move", player: this.player, entities: my.priests, x: cx, y: cy });
      }
    }
    // un-committed units MUSTER at home so they pack up before the next wave
    const waiting = army.filter((u) => !this.mem.committed.has(u));
    if (waiting.length > 0 && my.tcCenter) {
      dispatch({ type: "move", player: this.player, entities: waiting, x: my.tcCenter.x, y: my.tcCenter.y });
    }
  }

  private buildingRoles(reg: Registry, civ: string): BuildingRoles {
    const all = reg.buildingsOf(civ);
    const ids = (match: (b: BuildingDef) => boolean): string[] =>
      all.filter(match).map((b) => b.id).sort();
    const trains = (b: BuildingDef, cls: string) => reg.unitsOf(civ).some((u) => u.trainingBuilding === b.id && u.class === cls);
    return {
      townCenter: ids((b) => !!b.isCore),
      housing: ids((b) => !b.isCore && (b.population ?? 0) > 0),
      barracks: ids((b) => trains(b, "infantry") || trains(b, "ranged") || trains(b, "siege")),
      stables: ids((b) => trains(b, "cavalry")),
      temple: ids((b) => trains(b, "priest")),
      tower: ids((b) => !!b.garrison && !!b.attack),
      field: ids((b) => b.gatherResource === "food"),
      storage: ids((b) => !!b.isDropSite && !b.isCore && b.placement !== "waterEdge"),
    };
  }

  /** Rotate equivalent data-defined buildings by choosing the least-built role
   * member. Additive mod content therefore enters strategy without replacing a
   * shipped id; lexical order only breaks an exact count tie. */
  private nextBuilding(world: World, ids: string[]): string | null {
    if (!ids.length) return null;
    const counts = new Map(ids.map((id) => [id, 0]));
    for (const [eid, ident] of world.ident) {
      if (world.owner.get(eid)?.player !== this.player || ident.kind !== "building" || !counts.has(ident.defId)) continue;
      counts.set(ident.defId, counts.get(ident.defId)! + 1);
    }
    return [...ids].sort((a, b) => counts.get(a)! - counts.get(b)! || (a < b ? -1 : a > b ? 1 : 0))[0];
  }

  private myEntities(world: World, roles: BuildingRoles) {
    const workers: Eid[] = [];
    const army: Eid[] = [];
    const navy: Eid[] = [];
    const priests: Eid[] = [];
    const townCenters: Eid[] = [];
    const barracks: Eid[] = [];
    const builders: Eid[] = [];
    const storagePits: Eid[] = [];
    const fields: Eid[] = [];
    const stables: Eid[] = [];
    const temples: Eid[] = [];
    const towers: Eid[] = [];
    const dropSites: { x: number; y: number }[] = [];
    const foundations: Eid[] = [];
    let pendingFoundation = false;
    for (const [eid, ident] of world.ident) {
      if (world.owner.get(eid)?.player !== this.player) continue;
      if (ident.kind === "unit") {
        if (ident.cls === "worker") {
          workers.push(eid);
          if (world.builder.has(eid)) builders.push(eid);
        } else if (ident.cls === "priest") {
          priests.push(eid); // healers — kept out of the attack wave
        } else if (ident.cls === "naval") {
          navy.push(eid); // ships never join the LAND attack wave (they'd strand on the coast)
        } else {
          army.push(eid);
        }
      } else if (ident.kind === "building") {
        const b = world.building.get(eid);
        if (!b) continue;
        if (b.isFoundation) { pendingFoundation = true; foundations.push(eid); continue; }
        if (roles.townCenter.includes(ident.defId)) townCenters.push(eid);
        if (roles.barracks.includes(ident.defId)) barracks.push(eid);
        if (roles.storage.includes(ident.defId)) storagePits.push(eid);
        if (roles.field.includes(ident.defId)) fields.push(eid);
        if (roles.stables.includes(ident.defId)) stables.push(eid);
        if (roles.temple.includes(ident.defId)) temples.push(eid);
        if (roles.tower.includes(ident.defId)) towers.push(eid);
        if (b.isDropSite) {
          const t = world.transform.get(eid);
          if (t) dropSites.push({ x: t.pos.x, y: t.pos.y });
        }
      }
    }
    const tcCenter = townCenters[0] ? world.transform.get(townCenters[0])?.pos : null;
    return { workers, army, navy, priests, townCenters, barracks, builders, storagePits, fields, stables, temples, towers, foundations, dropSites, tcCenter, pendingFoundation };
  }

  private enemyTarget(world: World): { x: number; y: number } | null {
    const myTeam = world.players.get(this.player)?.team;
    let best: { x: number; y: number } | null = null;
    let bestD = Infinity;
    for (const [eid, ident] of world.ident) {
      if (ident.kind !== "building") continue;
      const o = world.owner.get(eid)?.player;
      if (o === this.player || o === undefined) continue;
      if (world.players.get(o)?.team === myTeam) continue; // don't march on allies
      const tr = world.transform.get(eid);
      if (!tr) continue;
      const b = world.building.get(eid);
      if (b?.isFoundation) continue;
      // prefer core buildings, which are the data-driven equivalent of a town centre.
      const score = b?.isCore ? 0 : 1;
      if (score < bestD) {
        bestD = score;
        best = { x: tr.pos.x, y: tr.pos.y };
      }
    }
    return best;
  }

  private bestNode(world: World, worker: Eid, prefer: string): Eid | null {
    const tr = world.transform.get(worker);
    if (!tr) return null;
    let best: Eid | null = null;
    let bestD = Infinity;
    let fallback: Eid | null = null;
    let fallbackD = Infinity;
    for (const [eid, r] of world.resource) {
      const nTr = world.transform.get(eid);
      if (!nTr) continue;
      // skip nodes this worker can't reach — a land worker must never be sent to
      // a fish school on the water (it can't path there and gets stuck on the shore).
      if (!canGatherNode(world, worker, eid)) continue;
      const d = (nTr.pos.x - tr.pos.x) ** 2 + (nTr.pos.y - tr.pos.y) ** 2;
      if (r.resource === prefer) {
        if (d < bestD) {
          bestD = d;
          best = eid;
        }
      } else {
        if (d < fallbackD) {
          fallbackD = d;
          fallback = eid;
        }
      }
    }
    return best ?? fallback;
  }

  private buildNear(
    world: World,
    reg: Registry,
    my: ReturnType<Bot["myEntities"]>,
    buildingId: string,
    dispatch: (cmd: Command) => void,
    anchor?: { x: number; y: number }
  ) {
    const at = anchor ?? my.tcCenter;
    if (!at) return;
    if (my.builders.length === 0) return;
    const ps = world.players.get(this.player)!;
    const def = reg.building(ps.civ, buildingId);
    for (const [r, amt] of Object.entries(def.cost))
      if ((ps.resources[r] ?? 0) < (amt ?? 0)) return;

    // Tiles we must NOT build on, so the base stays navigable: a 2-ring clear
    // APRON around every drop-off (otherwise the bot walls in its own Town
    // Center and workers can't deposit), and a 1-tile gap around every other
    // building (so there are always lanes to walk between them).
    const noBuild = new Set<number>();
    const reserveAround = (col: number, row: number, rings: number) => {
      const seenR = new Set<number>([world.hkey(col, row)]);
      noBuild.add(world.hkey(col, row));
      let fr: [number, number][] = [[col, row]];
      for (let i = 0; i < rings; i++) {
        const nx: [number, number][] = [];
        for (const [c, r] of fr) for (const nb of world.neighbors(c, r)) {
          const k = world.hkey(nb[0], nb[1]);
          if (seenR.has(k)) continue;
          seenR.add(k); noBuild.add(k); nx.push(nb);
        }
        fr = nx;
      }
    };
    for (const [eid, b] of world.building) {
      if (world.owner.get(eid)?.player !== this.player) continue;
      const t = world.transform.get(eid);
      if (!t) continue;
      const h = world.hexAtWorld(t.pos.x, t.pos.y);
      if (h) reserveAround(h.col, h.row, b.isDropSite && !b.isFoundation ? 2 : 1);
    }

    // BFS rings outward from the anchor for the first free, non-reserved hex.
    const start = world.hexAtWorld(at.x, at.y);
    if (!start) return;
    const seen = new Set<number>([world.hkey(start.col, start.row)]);
    let frontier: [number, number][] = [[start.col, start.row]];
    for (let ring = 0; ring < 14 && frontier.length; ring++) {
      const next: [number, number][] = [];
      for (const [c, r] of frontier) {
        for (const [nc, nr] of world.neighbors(c, r)) {
          const k = world.hkey(nc, nr);
          if (seen.has(k)) continue;
          seen.add(k);
          if (world.isWalkable(nc, nr) && !noBuild.has(k)) {
            const center = world.hexCenter(nc, nr);
            // place only — staffing the foundation is handled centrally so a
            // later build order can't yank this worker off and orphan the site.
            dispatch({ type: "build", player: this.player, workers: [], buildingId, x: center.x, y: center.y });
            return;
          }
          next.push([nc, nr]);
        }
      }
      frontier = next;
    }
  }
}
